import React, { useState } from 'react';

const FilterSidebar = () => {
  // State for filter values
  const [filters, setFilters] = useState({
    currentRole: 'C-Suite (CTO, CIO, etc.)',
    companySize: 'Enterprise ($1B+)',
    projectSize: 'Large ($1M+)',
    industry: 'Technology',
    techAlignment: 'High Match',
    lastContact: 'This Week'
  });

  // State for expanded/collapsed filter sections
  const [expandedSections, setExpandedSections] = useState({
    currentRole: true,
    companySize: true,
    projectSize: true,
    industry: true,
    techAlignment: true,
    lastContact: true
  });

  // Mock data for filter options
  const filterOptions = {
    currentRole: ['C-Suite (CTO, CIO, etc.)', 'VP', 'Director', 'Manager', 'Individual Contributor'],
    companySize: ['Enterprise ($1B+)', 'Mid-Market ($100M-$1B)', 'SMB ($10M-$100M)', 'Startup (<$10M)'],
    projectSize: ['Large ($1M+)', 'Medium ($500K-$1M)', 'Small ($100K-$500K)', 'Micro (<$100K)'],
    industry: ['Technology', 'Finance', 'Healthcare', 'Manufacturing', 'Retail', 'Education', 'Government'],
    techAlignment: ['High Match', 'Medium Match', 'Low Match'],
    lastContact: ['This Week', 'This Month', 'This Quarter', 'This Year', 'Never']
  };

  // Handle filter change
  const handleFilterChange = (filterName, value) => {
    setFilters({
      ...filters,
      [filterName]: value
    });
  };

  // Toggle section expansion
  const toggleSection = (section) => {
    setExpandedSections({
      ...expandedSections,
      [section]: !expandedSections[section]
    });
  };

  // Handle apply filters
  const handleApplyFilters = () => {
    console.log('Applied filters:', filters);
    // In a real implementation, this would trigger filtering of the data
  };

  // Handle reset filters
  const handleResetFilters = () => {
    setFilters({
      currentRole: 'C-Suite (CTO, CIO, etc.)',
      companySize: 'Enterprise ($1B+)',
      projectSize: 'Large ($1M+)',
      industry: 'Technology',
      techAlignment: 'High Match',
      lastContact: 'This Week'
    });
  };

  // Render a filter section
  const renderFilterSection = (title, name, options) => {
    return (
      <div className="mb-5 border-b border-gray-200 pb-4 last:border-0">
        <div 
          className="flex justify-between items-center cursor-pointer group"
          onClick={() => toggleSection(name)}
        >
          <h3 className="text-md font-medium text-gray-700">{title}</h3>
          <div className="text-gray-400 group-hover:text-primary transition-colors">
            {expandedSections[name] ? (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7"></path>
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
              </svg>
            )}
          </div>
        </div>
        
        {expandedSections[name] && (
          <div className="mt-3">
            <div className="relative mt-1 rounded-md shadow-sm">
              <select
                className="block w-full pl-3 pr-10 py-2.5 text-base border border-gray-300 bg-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-200"
                value={filters[name]}
                onChange={(e) => handleFilterChange(name, e.target.value)}
              >
                {options.map((option, index) => (
                  <option key={index} value={option}>{option}</option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 9l4-4 4 4m0 6l-4 4-4-4"></path>
                </svg>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="dashboard-card">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-800">Lead Qualification Filters</h2>
        <button 
          onClick={handleResetFilters}
          className="text-sm text-gray-500 hover:text-primary transition-colors"
        >
          Reset
        </button>
      </div>
      
      <div className="space-y-1">
        {/* Current Role Filter */}
        {renderFilterSection('Current Role', 'currentRole', filterOptions.currentRole)}
        
        {/* Company Size Filter */}
        {renderFilterSection('Company Size (Revenue)', 'companySize', filterOptions.companySize)}
        
        {/* Potential Project Size Filter */}
        {renderFilterSection('Potential Project Size', 'projectSize', filterOptions.projectSize)}
        
        {/* Industry Filter */}
        {renderFilterSection('Industry', 'industry', filterOptions.industry)}
        
        {/* Technology Alignment Filter */}
        {renderFilterSection('Technology Alignment', 'techAlignment', filterOptions.techAlignment)}
        
        {/* Last Contact Filter */}
        {renderFilterSection('Last Contact', 'lastContact', filterOptions.lastContact)}
      </div>
      
      {/* Active Filters Summary */}
      <div className="mt-6 bg-blue-50 p-3 rounded-lg">
        <h3 className="text-sm font-medium text-blue-800 mb-2">Active Filters</h3>
        <div className="flex flex-wrap gap-2">
          {Object.entries(filters).map(([key, value]) => (
            <div key={key} className="bg-white px-2 py-1 rounded-full text-xs border border-blue-200 flex items-center">
              <span className="text-blue-800">{value}</span>
              <button 
                className="ml-1 text-blue-400 hover:text-blue-600"
                onClick={() => handleFilterChange(key, filterOptions[key][0])}
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
          ))}
        </div>
      </div>
      
      {/* Apply Filters Button */}
      <button 
        onClick={handleApplyFilters}
        className="w-full mt-6 bg-primary text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition duration-200 flex items-center justify-center font-medium"
      >
        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"></path>
        </svg>
        Apply Filters
      </button>
    </div>
  );
};

export default FilterSidebar;
